import { Search, Timer } from 'lucide-react'
import { Input } from '../../lib/shadcn/input'
import { Spinner } from '../../lib/shadcn/spinner'
import { cn } from '../../lib/shadcn/utils'
import { Chip } from '../interviews/Chip'
import { kindLabel, kindTone, listTime, windowLeft, type Conversation } from '../../utils/whatsapp'

export type InboxFilter = 'open' | 'all' | 'handled'

const FILTERS: Array<{ key: InboxFilter; label: string }> = [
  { key: 'open', label: 'Open' },
  { key: 'all', label: 'All' },
  { key: 'handled', label: 'Handled' },
]

const EMPTY: Record<InboxFilter, string> = {
  open: 'Nothing is waiting. Every message that has come in has been handled.',
  all: 'No WhatsApp messages have come in yet.',
  handled: 'No conversation has been marked handled yet.',
}

export function ConversationList({
  rows,
  loading,
  loaded,
  filter,
  onFilter,
  search,
  onSearch,
  selected,
  onSelect,
}: {
  rows: Conversation[]
  loading: boolean
  loaded: boolean
  filter: InboxFilter
  onFilter: (f: InboxFilter) => void
  search: string
  onSearch: (s: string) => void
  selected: string | null
  onSelect: (phone: string) => void
}) {
  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="space-y-2 border-b border-border p-3">
        <div className="inline-flex w-full rounded-md border border-border bg-background p-0.5">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => onFilter(f.key)}
              className={cn(
                'flex-1 rounded-[5px] px-3 py-1.5 text-xs font-medium transition-colors',
                filter === f.key
                  ? 'bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            placeholder="Search by name or phone"
            className="h-8 pl-8 text-sm"
            aria-label="Search conversations by name or phone"
          />
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {loading && !loaded && (
          <div className="flex items-center justify-center gap-2 p-8 text-sm text-muted-foreground">
            <Spinner className="h-4 w-4" /> Reading conversations…
          </div>
        )}
        {loaded && rows.length === 0 && (
          <div className="p-8 text-center text-sm text-muted-foreground">
            {search.trim() ? 'No conversation matches that search.' : EMPTY[filter]}
          </div>
        )}
        <ul>
          {rows.map((c) => {
            const w = windowLeft(c)
            const active = selected === c.phone
            return (
              <li key={c.phone}>
                <button
                  type="button"
                  onClick={() => onSelect(c.phone)}
                  className={cn(
                    'flex w-full flex-col gap-1 border-b border-border/60 px-3 py-2.5 text-left transition-colors hover:bg-accent',
                    active && 'bg-[hsl(var(--brand-soft))]/40',
                  )}
                >
                  <div className="flex items-center gap-2">
                    <span
                      className={cn(
                        'min-w-0 flex-1 truncate text-sm',
                        c.unread_count > 0 ? 'font-semibold text-foreground' : 'text-foreground',
                      )}
                    >
                      {c.display_name}
                    </span>
                    <span className="shrink-0 text-[11px] tabular-nums text-muted-foreground">
                      {listTime(c.last_message_at)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Chip tone={kindTone(c.contact_kind)} className="px-2 py-0 text-[10px]">
                      {kindLabel(c.contact_kind)}
                    </Chip>
                    <span
                      title={
                        w.open
                          ? 'Time left to reply freely. After that, only an approved template can be sent.'
                          : 'The 24 hour window has closed. Only an approved template can be sent.'
                      }
                      className={cn(
                        'inline-flex items-center gap-0.5 text-[10px] tabular-nums',
                        w.open ? 'text-emerald-700 dark:text-emerald-300' : 'text-muted-foreground/70',
                      )}
                    >
                      <Timer className="h-3 w-3" />
                      {w.label}
                    </span>
                    {c.unread_count > 0 && (
                      <span
                        aria-label={`${c.unread_count} unhandled`}
                        className="ml-auto inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-[hsl(var(--brand))] px-1.5 text-[11px] font-semibold text-[hsl(var(--brand-foreground))]"
                      >
                        {c.unread_count}
                      </span>
                    )}
                  </div>
                  <p className="truncate text-xs text-muted-foreground">
                    {c.last_direction === 'out' && <span className="text-muted-foreground/80">You: </span>}
                    {c.last_message_preview ?? ''}
                  </p>
                </button>
              </li>
            )
          })}
        </ul>
      </div>
    </div>
  )
}
